/**
 * Step 1: Request calendar permission
 *
 * Asks the user to grant device calendar access.
 * Shows feature highlights explaining why access is needed.
 */

import { useState } from 'react';
import { View, Text, StyleSheet, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Calendar from 'expo-calendar';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getTypographyStyle } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { OnboardingStepLayout } from '../OnboardingStepLayout';

type PermissionStepProps = {
  onContinue: () => void;
  onSkip?: () => void;
};

const FEATURE_HIGHLIGHTS = [
  'View events from all your calendars',
  'Create and edit events easily',
  'AI-powered scheduling assistance',
] as const;

export function PermissionStep({ onContinue, onSkip }: PermissionStepProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const { status } = await Calendar.requestCalendarPermissionsAsync();
      const granted = status === 'granted';
      setHasPermission(granted);

      if (granted) {
        setTimeout(() => onContinue(), 500);
      } else {
        Alert.alert(
          'Permission Denied',
          'Calendar access is required to view and manage your events. Please enable it in your device settings.',
          [
            { text: 'Skip for Now', onPress: onSkip, style: 'cancel' },
            { text: 'Try Again', onPress: handleRequestPermission },
          ],
        );
      }
    } catch (error) {
      console.error('[PermissionStep] Error requesting permission:', error);
      Alert.alert('Error', 'Failed to request calendar permission');
    } finally {
      setIsRequesting(false);
    }
  };

  const buttonLabel = isRequesting
    ? 'Requesting...'
    : hasPermission
      ? 'Permission Granted!'
      : 'Grant Calendar Access';

  return (
    <OnboardingStepLayout
      icon={
        <Ionicons name="calendar-outline" size={80} color={palette.primary} />
      }
      title="Access Your Calendars"
      subtitle="FimFam needs access to your device calendars to view and manage your events with AI assistance."
      primaryButton={{
        label: buttonLabel,
        onPress: handleRequestPermission,
        disabled: isRequesting || hasPermission,
      }}
      onSkip={onSkip}
    >
      <View style={styles.featureList}>
        {FEATURE_HIGHLIGHTS.map((text) => (
          <View key={text} style={styles.featureItem}>
            <Ionicons
              name="checkmark-circle"
              size={24}
              color={palette.primary}
            />
            <Text style={[styles.featureText, { color: palette.text }]}>
              {text}
            </Text>
          </View>
        ))}
      </View>
    </OnboardingStepLayout>
  );
}

const bodyStyle = getTypographyStyle('body');

const styles = StyleSheet.create({
  featureList: {
    width: '100%',
    paddingTop: Spacing.lg,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Spacing.lg,
    paddingHorizontal: Spacing.xl,
  },
  featureText: {
    ...bodyStyle,
    fontSize: 14,
    lineHeight: 20,
    marginLeft: Spacing.md,
    flex: 1,
  },
});
